<template>
  <widget-content
    variant="table"
    @refresh="$emit('refresh')"
  >
    <div class="input-group mb-3">
      <div class="input-group-prepend">
        <span class="input-group-text"><i class="fa fa-search"></i></span>
      </div>
      <b-form-input
        v-model="filter"
        class="form-control"
        type="text"
        :placeholder="`Search...`"
        debounce="500"
      ></b-form-input>
    </div>
    <b-table
      :id="`table-widget-${_uid}`"
      ref="table"
      class="mb-0"
      bordered
      :fixed="fixed"
      :stacked="stacked"
      :striped="striped"
      :fields="fields"
      :filter="filter"
      :per-page="perPage"
      :sticky-header="stickyHeader"
      :current-page="currentPage"
      :items="items"
      :sort-by="options.sortBy"
      :sort-desc="options.sortDesc"
      :responsive="responsive"
      @refreshed="onTableRefreshed"
    >
      <template v-slot:table-colgroup="scope">
        <col
          v-for="(width, key) in fieldWidths"
          :key="key"
          :style="{ width: width }"
        >
      </template>
      <template
        v-for="field in htmlFormattedHeaders"
        :slot="`head(${field})`"
        slot-scope="data"
      >
        <div v-html="data.label"></div>
      </template>
      <template
        v-for="field in htmlFormattedFooters"
        :slot="`foot(${field})`"
        slot-scope="data"
      >
        <div v-html="data.label"></div>
      </template>
      <template
        v-for="field in htmlFormattedFields"
        :slot="`cell(${field})`"
        slot-scope="data"
      >
        <component
          :is="data.value.component.name"
          v-if="data.value.component"
          :key="`${field}_component`"
          v-bind="data.value.component.props"
        />
        <div
          v-else
          :key="`${field}_value`"
          v-html="data.value.html ? data.field.html : data.value"
        ></div>
      </template>
    </b-table>
    <div
      v-if="perPage > 0 && totalRows > perPage"
      class="mt-3 d-flex align-items-center"
    >
      <button
        v-if="allowShowAll"
        class="btn btn-white mr-2"
        @click.prevent="perPage = totalRows"
      >
        Show All
      </button>
      <div>
        <b-pagination
          v-model="currentPage"
          pills
          class="mb-0"
          aria-controls="`table-widget-${_uuid}`"
          align="left"
          :total-rows="totalRows"
          :per-page="perPage"
        >
          <template v-slot:first-text>
            First
          </template>
          <template v-slot:prev-text>
            <i class="fa fa-chevron-left"></i>
          </template>
          <template v-slot:next-text>
            <i class="fa fa-chevron-right"></i>
          </template>
          <template v-slot:last-text>
            Last
          </template>
        </b-pagination>
      </div>
    </div>
  </widget-content>
</template>

<style lang="scss" scoped>
.input-group {
  .input-group-text {
    background-color: transparent;
  }
  .form-control {
    border-left: 0;
  }
}
th {
  font-size: 12px;
}
</style>

<script>
import $ from 'jquery'
import { get, merge } from 'lodash'
import { loadWidget } from '@/api'
import axios from 'axios'
import WidgetContent from './components/WidgetContent'
import ReconcileLeadButton from '@/components/ReconcileLeadButton'
import PersonActionDropdown from '@/components/PersonActionDropdown'
import Person from '@/components/roster/Person'

const CancelToken = axios.CancelToken

export default {
  name: 'TableWidget',

  components: {
    PersonActionDropdown,
    ReconcileLeadButton,
    WidgetContent,
    Person
  },

  props: {
    title: {
      type: String,
      default: ''
    },

    type:{
      type: String,
      required: true
    },

    filterValues: {
      type: [Array, Object],
      default () {
        return []
      }
    },

    explicitFilter: {
      type: Object,
      default () {
        return {}
      }
    },

    config: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      index: null,
      filter: '',
      lookup: {},
      fixed: false,
      responsive: false,
      stacked: false,
      currentPage: 1,
      perPage: 10,
      totalRows: null,
      striped: false,
      allowShowAll: true,
      stickyHeader: null,
      fields: [],
      items: null,
      fieldWidths: {},
      htmlFormattedHeaders: [],
      htmlFormattedFooters: [],
      htmlFormattedFields: [],
      options: {},
      cancel: null
    }
  },

  watch: {
    config: {
      handler () {
        this.loadConfig(this.config)
      },
      deep: true
    },
    currentPage () {

    },
    filter () {
      this.currentPage = 1
    }
  },

  mounted () {
    this.loadConfig(this.config)
  },

  updated () {

    // this.bindEvents()
  },

  methods: {
    loadConfig (config) {
      this.perPage = get(config, 'data.options.perPage')
      this.totalRows = get(config, 'data.totalRows')
      this.fixed = get(config, 'data.options.fixed')
      this.responsive = get(config, 'data.options.responsive')
      this.stacked = get(config, 'data.options.stacked')
      this.striped = get(config, 'data.options.striped')
      this.fields = get(config, 'data.fields') || []
      this.htmlFormattedHeaders = get(config, 'data.options.htmlFormattedHeaders', [])
      this.htmlFormattedFooters = get(config, 'data.options.htmlFormattedFooters', [])
      this.stickyHeader = get(config, 'data.options.stickyHeader')
      this.fieldWidths = get(config, 'data.options.fieldWidths', {})
      this.htmlFormattedFields = get(config, 'data.options.htmlFormattedFields', [])
      this.allowShowAll = this.paginator !== 'server' && get(config, 'data.options.allowShowAll')
      this.paginator = get(config, 'data.options.paginator')
      this.options = get(config, 'data.options')

      if (this.paginator === 'server') {
        this.items = (context, callback) => {
          this.cancel && this.cancel()
          loadWidget(
            this.type,
            merge(
              {},
              this.filterValues,
              this.explicitFilter,
              {
                'paginator': merge(
                  context,
                  {
                    currentPage: this.currentPage
                  }
                )
              }
            ),
            false,
            new CancelToken((c) => {
              this.cancel = c
            })
          ).then((response) => {
            this.totalRows = response.data.data.totalRows
            callback(response.data.data.items)
          })
        }
      } else {
        this.items = get(this.config, 'data.items') || []
      }

      this.$refs['table'].refresh()
    },
    onTableRefreshed () {
      this.$nextTick(() => {
        $(this.$el).find('[data-toggle="tooltip"]').tooltip()
      })
    }
  }
}
</script>